/**
 * Dialog: limpiar residuos pequeños en Alegra (saldos de redondeo).
 * Extraído de CxPPanel.jsx en Iter 236.m.14 (Carlos).
 */
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Trash2 } from 'lucide-react';

const fmt = (n) => `$${Math.round(n || 0).toLocaleString('es-CL')}`;
const fmtFecha = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso + 'T12:00:00');
  return d.toLocaleDateString('es-CL', { day: '2-digit', month: 'short', year: 'numeric' });
};

export function CleanupResiduosDialog({
  open,
  preview,
  busy,
  montoMax,
  setMontoMax,
  onClose,
  onReload,
  onConfirm,
}) {
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <DialogContent className="max-w-2xl" data-testid="dialog-cleanup-residuos">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trash2 className="h-4 w-4 text-red-600" /> Limpiar residuos pequeños en Alegra
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <p className="text-xs text-muted-foreground">
            Cierra automáticamente facturas pendientes con saldo muy bajo (residuos típicos de redondeo
            en Alegra) empujando un payment-out por cada una y marcándolas pagadas localmente.
          </p>
          <div className="flex items-center gap-2">
            <Label className="text-xs">Saldo máximo:</Label>
            <Input
              type="number"
              step="1"
              value={montoMax}
              onChange={(e) => setMontoMax(e.target.value)}
              onBlur={onReload}
              className="w-24 h-7 text-xs"
              data-testid="input-cleanup-monto-max"
            />
            <span className="text-xs text-muted-foreground">
              CLP · solo facturas con &gt;90 días de antigüedad
            </span>
          </div>

          {!preview ? (
            <p className="text-xs text-muted-foreground">Cargando preview…</p>
          ) : preview.count === 0 ? (
            <p className="text-sm text-emerald-700 font-medium">
              No hay residuos que limpiar con esos criterios. ✓
            </p>
          ) : (
            <>
              <div className="text-sm bg-red-50 dark:bg-red-950/30 p-2 rounded border border-red-200">
                <b>{preview.count}</b> facturas · Total a cerrar:{' '}
                <b className="tabular-nums">{fmt(preview.total)}</b>
              </div>
              <div className="max-h-72 overflow-y-auto border rounded">
                <table className="w-full text-xs">
                  <thead className="bg-muted/40 sticky top-0">
                    <tr>
                      <th className="text-left p-1.5">Proveedor</th>
                      <th className="text-left p-1.5">Folio</th>
                      <th className="text-right p-1.5">Monto</th>
                      <th className="text-left p-1.5">Emisión</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.facturas.map((f) => (
                      <tr key={f.id} className="border-t">
                        <td className="p-1.5 truncate max-w-[260px]" title={f.nombre_proveedor}>
                          {f.nombre_proveedor}
                        </td>
                        <td className="p-1.5 font-mono">{f.folio}</td>
                        <td className="p-1.5 text-right tabular-nums">{fmt(f.monto)}</td>
                        <td className="p-1.5">{fmtFecha(f.fecha_emision)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button size="sm" variant="outline" onClick={onClose} data-testid="btn-cleanup-cancel">
              Cancelar
            </Button>
            <Button
              size="sm"
              disabled={!preview || preview.count === 0 || busy}
              onClick={onConfirm}
              className="bg-red-600 hover:bg-red-700 gap-1.5"
              data-testid="btn-cleanup-confirm"
            >
              <Trash2 className="h-3.5 w-3.5" />
              {busy ? 'Cerrando…' : `Cerrar ${preview?.count || 0} residuos en Alegra`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
